<script setup lang="ts">
import { useSwiper } from '~/composables/common/use-swiper'
import type { DropdownItem } from '~/composables/common/interface'

const { providers, selectedProvider } = defineProps<{ providers: DropdownItem[]; selectedProvider?: string }>()

const emit = defineEmits<{ select: [selected: string] }>()

const { modules } = useSwiper()
</script>

<template>
  <div class="providers-filter">
    <Swiper
      :modules="modules"
      :navigation="{ nextEl: '.providers-filter__next', prevEl: '.providers-filter__prev' }"
      slidesPerView="auto"
      spaceBetween="10"
      :initialSlide="providers.findIndex(item => item.value === selectedProvider)"
    >
      <div class="providers-filter__arrow providers-filter__prev">
        <div class="providers-filter__arrow__icon icon-chevron-left"></div>
      </div>

      <SwiperSlide v-for="provider in providers" :key="provider.value">
        <div
          :class="['providers-filter__item', { 'provider-selected': selectedProvider === provider.value }]"
          @click="emit('select', provider.value?.toString() ?? '')"
        >
          <NuxtImg :src="provider.icon" width="24" :alt="provider.value?.toString()" class="provider-icon"/>
          <NuxtImg :src="provider.iconGray" width="24" :alt="provider.value?.toString()" class="provider-icon--gray"/>
          <span>{{ provider.label }}</span>
          <span v-if="provider.labelStatement" class="provider-label">{{ provider.labelStatement }}</span>
        </div>
      </SwiperSlide>

      <div class="providers-filter__arrow providers-filter__next">
        <div class="providers-filter__arrow__icon icon-chevron-right"></div>
      </div>
    </Swiper>
  </div>
</template>

<style scoped lang="scss">
.providers-filter {
  position: relative;
  margin-top: -6px;

  &__arrow {
    position: absolute;
    top: 6px;
    z-index: 1;
    height: 100%;

    &__icon {
      filter: brightness(0) saturate(100%) invert(34%) sepia(0%) saturate(241%) hue-rotate(136deg) brightness(99%)
        contrast(94%);
    }

    &.swiper-button-disabled {
      display: none;
    }
  }

  &__prev {
    padding: 14px 46px 14px 8px;
    background-image: linear-gradient(90deg, $body-bg 25%, rgba(255, 255, 255, 0) 100%);
  }

  &__next {
    right: 0;
    padding: 14px 8px 14px 46px;
    background: linear-gradient(-90deg, $body-bg 25%, rgba(255, 255, 255, 0) 100%);
  }

  &:deep(.swiper) {
    padding-top: 6px;
    .swiper-slide {
      width: auto;
    }
  }

  &__item {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px 12px;
    border: 1.5px solid $white;
    border-radius: 4px;
    font-weight: $font-weight-medium;
    color: $neutral-800;
    background-color: $white;
    box-shadow: 0px 4px 8px 0px rgba(0, 0, 0, 0.02);
    cursor: pointer;
    transition: 0.3s;

    .provider-icon {
      display: none;
    }

    &:hover,
    &.provider-selected {
      border-color: $primary-color;
      color: $primary-color;

      .provider-icon {
        display: inline;

        &--gray {
          display: none;
        }
      }
    }

    .provider-label {
      position: absolute;
      top: -6px;
      right: 12px;
      z-index: 1;
      font-size: 12px;
      font-weight: 700;
      line-height: 16px;
      color: $white;

      &::before {
        content: '';
        position: absolute;
        left: -6px;
        z-index: -1;
        width: 5px;
        height: 6px;
        transform: skewX(-24deg);
        background-image: linear-gradient(to top, #564e02 0%, #a17e01 100%);
      }

      &::after {
        content: '';
        position: absolute;
        left: -4px;
        z-index: -1;
        width: 43px;
        height: 100%;
        background-image: linear-gradient(98deg, #e8a805 20%, #957400 95%);
        clip-path: path('M31.9 16H11.1C7.7 16 4.6 13.7 3.5 10.3L0 0H43L39.5 10.3C38.4 13.7 35.3 16 31.9 16Z');
      }
    }
  }
}
</style>
